import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translation } from 'react-i18next';

import { validators } from '@mssgme/shared';
import { FormErrors, FormField } from '@mssgme/ui';

import styles from './styles.scss';

const password = validators.passwordValidator({
    minLength: 6,
    maxLength: 40,
    arguablySafeLength: 20,
    required: [
        { weight: 50, fn: validators.shortenThan, message: 'password_length' },
        { weight: 0, fn: validators.longerThan, message: 'password_length' },
    ],
    optional: [{ weight: 50, fn: validators.lazyPassword, message: 'password_complexity' }],
});

export default class PasswordWithStrength extends React.Component {
    static propTypes = {
        label: PropTypes.string,
        value: PropTypes.string,
        id: PropTypes.string,
        name: PropTypes.string,
        placeholder: PropTypes.string,
        className: PropTypes.string,
        error: PropTypes.string,
        onChange: PropTypes.func,
        withLabel: PropTypes.bool,
        lazy: PropTypes.bool,
    };

    static getDerivedStateFromProps(props, state) {
        if (props.value !== state.initialPassword) {
            return {
                initialPassword: props.value,
                password: props.value || '',
            };
        }

        return null;
    }

    constructor(props) {
        super(props);

        this.state = {
            initialPassword: props.value,
            password: props.value || '',
            strength: 0,
            errors: null,
            warnings: [],
        };

        this.handleChange = this.handleChange.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        const value = this.state.password;

        if (value !== prevState.password) {
            const { error, lazy } = this.props;
            const skipChecks = lazy && !value;
            const errors = skipChecks ? [] : password.validate(value);
            const { total, weight } = password.estimate(errors);
            const strength = weight <= 0 || skipChecks ? 0 : weight >= total ? 2 : 1;
            const critical = errors.filter((e) => e.required);
            const warnings = errors.filter((e) => !e.required);

            this.setState(() => ({
                errors: (critical.length || error) && [error, ...critical].filter(Boolean),
                warnings,
                strength,
            }));
        }
    }

    handleChange(e) {
        this.setState({
            password: e.target.value,
        });

        if (this.props.onChange) {
            this.props.onChange(e);
        }
    }

    render() {
        const { className, withLabel, error, lazy, ...rest } = this.props;
        const { password, strength, errors, warnings } = this.state;

        return (
            <Translation>
                {(t) => (
                    <FormField
                        {...rest}
                        value={password}
                        errors={
                            errors && errors.map((e) => (typeof e === 'string' ? e : t(`errorsList.${e.message}`, e)))
                        }
                        type="password"
                        className={classNames(styles.root, className, styles[`strength-${strength}`])}
                        onChange={this.handleChange}
                    >
                        <div
                            className={classNames(styles.strengthMeter, styles[`strength-${strength}`], {
                                [styles.withLabel]: withLabel,
                            })}
                        >
                            {withLabel && t(`passwordStrength.${strength}`)}
                        </div>
                        {warnings.length ? (
                            <FormErrors error={warnings.map((e) => t(`errorsList.${e.message}`, e))} critical={false} />
                        ) : null}
                    </FormField>
                )}
            </Translation>
        );
    }
}
